import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Usb,
  Plug,
  Unplug,
  Zap,
  Wand2,
  Trash2,
  Copy,
  RefreshCw,
  Send,
  Info,
  Loader2,
  Terminal as TerminalIcon,
  Smartphone
} from 'lucide-react';
import {
  UsbSerialNative,
  isUsbSerialSupported,
  getUsbDeviceStatus,
  requestUsbPermission,
  connectUsbSerialAdvanced,
  disconnectUsbSerial,
  sendUsbCommand,
  sendUsbHex,
  setUsbControlLine,
  setUsbLineParameters,
  setUsbBreak,
  purgeUsbBuffers,
  getUsbLinkStats,
  describeUsbDevice,
  getUsbLogs,
  UsbDeviceItem,
  UsbLinkStats
} from '../services/usbSerial';
import { quickConnect, autoDetectBaud, bastetRpc } from '../services/fieldKit';
import { triggerHaptic } from '../services/purrSynthesizer';

type Chunk = { dir: 'RX' | 'TX' | 'SYS'; text: string; hex: string; ts: number };

const BAUDS = [300, 1200, 2400, 4800, 9600, 19200, 38400, 57600, 74880, 115200, 230400, 250000, 460800, 500000, 921600, 1000000, 2000000];
const FRAMINGS: Record<string, { label: string; dataBits: number; stopBits: number; parity: number }> = {
  '8N1': { label: '8N1', dataBits: 8, stopBits: 1, parity: 0 },
  '8E1': { label: '8E1', dataBits: 8, stopBits: 1, parity: 2 },
  '8O1': { label: '8O1', dataBits: 8, stopBits: 1, parity: 1 },
  '8N2': { label: '8N2', dataBits: 8, stopBits: 2, parity: 0 },
  '7E1': { label: '7E1', dataBits: 7, stopBits: 1, parity: 2 },
  '7O1': { label: '7O1', dataBits: 7, stopBits: 1, parity: 1 }
};
const ENDINGS: Record<string, string> = { none: '', cr: '\r', lf: '\n', crlf: '\r\n' };
const MAX_CHUNKS = 800;

const ROLE_STYLE: Record<string, string> = {
  serial: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  adb: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  hub: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  storage: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  audio: 'bg-pink-500/15 text-pink-300 border-pink-500/30',
  hid: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  other: 'bg-slate-500/15 text-slate-400 border-slate-500/30'
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const fmtBytes = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(2)} MB`);

export const UsbFieldConsole: React.FC = () => {
  const [devices, setDevices] = useState<UsbDeviceItem[]>([]);
  const [connected, setConnected] = useState(false);
  const [statusMsg, setStatusMsg] = useState('Not connected');
  const [busy, setBusy] = useState<string | null>(null);

  const [baud, setBaud] = useState(115200);
  const [framing, setFraming] = useState('8N1');
  const [dtr, setDtr] = useState(false);
  const [rts, setRts] = useState(false);

  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [view, setView] = useState<'text' | 'hex'>('text');
  const [input, setInput] = useState('');
  const [hexInput, setHexInput] = useState(false);
  const [ending, setEnding] = useState<keyof typeof ENDINGS>('crlf');
  const [stats, setStats] = useState<UsbLinkStats | null>(null);
  const [info, setInfo] = useState<string>('');
  const [adbCmd, setAdbCmd] = useState('getprop ro.product.model');
  const [adbOut, setAdbOut] = useState('');

  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const pendingRx = useRef<{ text: string; hex: string } | null>(null);

  const push = useCallback((c: Chunk) => {
    setChunks((prev) => {
      const next = prev.length >= MAX_CHUNKS ? prev.slice(prev.length - MAX_CHUNKS + 1) : prev.slice();
      next.push(c);
      return next;
    });
  }, []);
  const sys = useCallback((text: string) => push({ dir: 'SYS', text, hex: '', ts: Date.now() }), [push]);

  const refresh = useCallback(async () => {
    const st = await getUsbDeviceStatus();
    setDevices(st.devices || []);
    setConnected(st.connected);
    if (st.connected && st.baudRate) setBaud(st.baudRate);
    setStatusMsg(st.connected ? `Connected · ${st.baudRate} baud` : st.lastError && st.status === 'ERROR' ? st.lastError : 'Not connected');
  }, []);

  // Live wiring: data, status and hot-plug events straight from the native bridge.
  useEffect(() => {
    refresh();
    if (!isUsbSerialSupported()) return;
    const handles: any[] = [];
    const add = (ev: string, fn: (d: any) => void) =>
      UsbSerialNative.addListener(ev, fn)
        .then((h: any) => handles.push(h))
        .catch(() => {});
    add('usbDataReceived', (d) => {
      const p = pendingRx.current;
      if (p) {
        p.text += d.text || '';
        p.hex += (p.hex && d.hex ? ' ' : '') + (d.hex || '');
      } else {
        pendingRx.current = { text: d.text || '', hex: d.hex || '' };
      }
    });
    const flush = setInterval(() => {
      const p = pendingRx.current;
      if (!p) return;
      pendingRx.current = null;
      push({ dir: 'RX', text: p.text, hex: p.hex, ts: Date.now() });
    }, 120);
    add('usbStatusChanged', (d) => {
      setConnected(!!d.connected);
      setStatusMsg(d.message || d.status);
    });
    add('usbAttached', (d) => {
      sys(`Attached ${d.vendorId?.toString(16)}:${d.productId?.toString(16)}`);
      refresh();
    });
    add('usbDetached', () => {
      sys('Device detached');
      refresh();
    });
    return () => {
      clearInterval(flush);
      handles.forEach((h) => { try { h.remove(); } catch { /* gone */ } });
    };
  }, [refresh, push, sys]);

  // Throughput stats while linked
  useEffect(() => {
    if (!connected) {
      setStats(null);
      return;
    }
    let alive = true;
    const tick = async () => {
      const s = await getUsbLinkStats();
      if (alive && s) setStats(s);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [connected]);

  useEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [chunks, view]);

  const rendered = useMemo(() => {
    if (view === 'hex') {
      return chunks
        .map((c) => (c.dir === 'SYS' ? `-- ${c.text}` : `${c.dir} ${c.hex || Array.from(new TextEncoder().encode(c.text)).map((b) => b.toString(16).padStart(2, '0').toUpperCase()).join(' ')}`))
        .join('\n');
    }
    let s = '';
    for (const c of chunks) {
      if (c.dir === 'RX') s += c.text;
      else s += `${s && !s.endsWith('\n') ? '\n' : ''}${c.dir === 'TX' ? '» ' : '· '}${c.text}\n`;
    }
    return s;
  }, [chunks, view]);

  const run = async (name: string, fn: () => Promise<void>) => {
    if (busy) return;
    triggerHaptic(15);
    setBusy(name);
    try {
      await fn();
    } catch (e: any) {
      sys(`${name} failed: ${e?.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const onQuickConnect = () =>
    run('connect', async () => {
      sys('Plug & Play: finding device, requesting permission, detecting baud...');
      const r = await quickConnect({ autoBaud: true, dtr, rts });
      if (r.ok) {
        sys(`Connected ${r.productName || r.kind} ${r.kind === 'serial' ? `@ ${r.baudRate} baud` : '(ADB)'}`);
        if (r.detection && !r.detection.found) sys(r.detection.hint || 'No readable data yet, device may be waiting for input.');
      } else {
        sys(`Connect failed: ${r.error}`);
      }
      await refresh();
    });

  const onConnectDevice = (d: UsbDeviceItem) =>
    run('connect', async () => {
      if (!d.hasPermission) {
        sys(`Requesting permission for ${d.productName || d.vendorIdHex}...`);
        const ok = await requestUsbPermission(d.vendorId, d.productId);
        if (!ok) {
          sys('Permission denied.');
          await refresh();
          return;
        }
      }
      const f = FRAMINGS[framing];
      const r = await connectUsbSerialAdvanced({ deviceId: d.deviceId, vendorId: d.vendorId, productId: d.productId, baudRate: baud, dataBits: f.dataBits, stopBits: f.stopBits, parity: f.parity, dtr, rts });
      sys(r.success ? `Connected @ ${baud} baud ${framing}` : `Connect failed: ${r.error}`);
      await refresh();
    });

  const onDisconnect = () =>
    run('disconnect', async () => {
      await disconnectUsbSerial();
      await refresh();
    });

  const applyLine = (b: number, fr: string) =>
    run('line', async () => {
      if (!connected) return;
      const f = FRAMINGS[fr];
      const r: any = await setUsbLineParameters({ baudRate: b, dataBits: f.dataBits, stopBits: f.stopBits, parity: f.parity });
      sys(r.success ? `Line set to ${b} ${fr}` : `Line change failed: ${r.error}`);
    });

  const onAutoBaud = () =>
    run('autobaud', async () => {
      sys('Auto-detecting baud rate (probing with CR/LF)...');
      const r = await autoDetectBaud({});
      if (r.ok && r.found && r.bestBaud) {
        setBaud(r.bestBaud);
        sys(`Detected ${r.bestBaud} baud (confidence ${r.confidence}).`);
      } else {
        sys(r.error || r.hint || 'No readable data at any rate.');
      }
    });

  const onSend = async () => {
    if (!input && ending === 'none') return;
    triggerHaptic(10);
    if (hexInput) {
      const r = await sendUsbHex(input);
      if (!r.success) sys(`Send failed: ${r.error}`);
      else push({ dir: 'TX', text: `[hex] ${input.trim()}`, hex: '', ts: Date.now() });
    } else {
      const r = await sendUsbCommand(input + ENDINGS[ending]);
      if (!r.success) sys(`Send failed: ${r.error}`);
      else push({ dir: 'TX', text: input, hex: '', ts: Date.now() });
    }
    setInput('');
  };

  const pulseReset = () =>
    run('reset', async () => {
      await setUsbControlLine('RTS', true);
      await sleep(120);
      await setUsbControlLine('RTS', false);
      sys('Reset pulse sent on RTS');
    });

  // Classic esptool "enter bootloader, then run" line sequence (DTR→GPIO0, RTS→EN)
  const espBootloader = () =>
    run('esp', async () => {
      await setUsbControlLine('DTR', false);
      await setUsbControlLine('RTS', true);
      await sleep(100);
      await setUsbControlLine('DTR', true);
      await setUsbControlLine('RTS', false);
      await sleep(50);
      await setUsbControlLine('DTR', false);
      sys('ESP bootloader sequence sent (DTR/RTS)');
    });

  const toggleLine = async (line: 'DTR' | 'RTS') => {
    const next = line === 'DTR' ? !dtr : !rts;
    if (connected) {
      const r = await setUsbControlLine(line, next);
      if (!r.success) {
        sys(`${line} change failed: ${r.error}`);
        return;
      }
    }
    line === 'DTR' ? setDtr(next) : setRts(next);
  };

  const onBreak = () =>
    run('break', async () => {
      await setUsbBreak(true);
      await sleep(250);
      await setUsbBreak(false);
      sys('BREAK sent');
    });

  const onDescribe = (d: UsbDeviceItem) =>
    run('describe', async () => {
      const r = await describeUsbDevice({ deviceId: d.deviceId });
      const { rawDescriptorsHex, ...rest } = r as any;
      setInfo(JSON.stringify(rest, null, 2) + (rawDescriptorsHex ? `\n\nraw descriptors (${(r as any).rawDescriptorsLength} B):\n${rawDescriptorsHex}` : ''));
    });

  const copyAll = async () => {
    let logs: string[] = [];
    try {
      logs = await getUsbLogs(400);
    } catch {
      /* use local view */
    }
    const text = logs.length ? logs.join('\n') : rendered;
    try {
      await navigator.clipboard.writeText(text);
      sys('Log copied to clipboard');
    } catch {
      sys('Clipboard unavailable');
    }
  };

  const adbDevice = devices.find((d) => d.role === 'adb' || d.isAdb);
  const runAdb = (cmd: string) =>
    run('adb', async () => {
      setAdbOut(`$ ${cmd}\n...`);
      const r: any = await bastetRpc('usb.adb_exec', { command: cmd, timeout_ms: 12000 }).catch((e: any) => ({ ok: false, error: e?.message }));
      setAdbOut(`$ ${cmd}\n${r.ok ? r.output || '(no output)' : r.error || 'failed'}${r.timed_out ? '\n[timed out; command may still be running]' : ''}`);
    });

  const btn = 'px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-[11px] font-bold text-white disabled:opacity-40 flex items-center gap-1.5';

  return (
    <div className="space-y-4">
      {/* One-tap hook */}
      <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-bold text-white tracking-wider">
              <Usb className="w-4 h-4 text-emerald-400" /> FIELD CONSOLE
            </div>
            <p className={`text-[11px] font-mono truncate ${connected ? 'text-emerald-300' : 'text-slate-400'}`}>{statusMsg}</p>
          </div>
          {connected ? (
            <button onClick={onDisconnect} disabled={!!busy} className="px-3 py-2 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 text-xs font-bold flex items-center gap-1.5">
              <Unplug className="w-3.5 h-3.5" /> Disconnect
            </button>
          ) : (
            <button onClick={onQuickConnect} disabled={!!busy} className="px-3 py-2 rounded-xl bg-fissal-gold text-slate-950 text-xs font-bold flex items-center gap-1.5 disabled:opacity-60">
              {busy === 'connect' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Plug &amp; Play
            </button>
          )}
        </div>

        {/* Devices */}
        <div className="space-y-1.5">
          {devices.length === 0 && (
            <div className="text-[11px] text-slate-500 font-mono">No USB devices. Plug one in with an OTG cable; it will show up here by itself.</div>
          )}
          {devices.map((d) => (
            <div key={d.deviceId} className="rounded-xl bg-black/40 border border-white/10 p-2.5 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-bold text-slate-100 truncate">{d.productName || d.manufacturerName || 'USB device'}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {d.vendorIdHex}:{d.productIdHex} · {d.driverClass !== 'None' ? d.driverClass : d.className || 'no driver'}
                    {d.hasPermission ? '' : ' · no permission'}
                  </div>
                </div>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_STYLE[d.role || 'other'] || ROLE_STYLE.other}`}>{d.isConnected ? 'linked' : d.role || 'other'}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {d.isSupportedDriver && !d.isConnected && (
                  <button className={btn} disabled={!!busy} onClick={() => onConnectDevice(d)}>
                    <Plug className="w-3 h-3 text-emerald-400" /> Connect @ {baud}
                  </button>
                )}
                {!d.hasPermission && !d.isSupportedDriver && (
                  <button className={btn} disabled={!!busy} onClick={async () => { await requestUsbPermission(d.vendorId, d.productId); refresh(); }}>
                    Grant permission
                  </button>
                )}
                <button className={btn} disabled={!!busy} onClick={() => onDescribe(d)}>
                  <Info className="w-3 h-3 text-sky-400" /> Descriptors
                </button>
              </div>
            </div>
          ))}
          <button className="text-[10px] text-slate-400 underline underline-offset-2 flex items-center gap-1" onClick={refresh}>
            <RefreshCw className="w-3 h-3" /> refresh devices
          </button>
        </div>
      </div>

      {info && (
        <div className="glass-panel rounded-2xl p-3 border border-white/10 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-300 tracking-wider">DEVICE DESCRIPTORS</span>
            <button className="text-[10px] text-slate-400 underline" onClick={() => setInfo('')}>close</button>
          </div>
          <pre className="bg-black/60 rounded-lg p-2.5 text-[10px] text-sky-200 font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">{info}</pre>
          <button className={btn} onClick={() => navigator.clipboard?.writeText(info)}>
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      )}

      {/* Android / ADB control */}
      {adbDevice && (
        <div className="glass-panel rounded-2xl p-3.5 border border-sky-500/30 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-bold text-white tracking-wider">
            <Smartphone className="w-4 h-4 text-sky-400" /> ANDROID DEVICE (USB ADB)
          </div>
          <p className="text-[10px] text-slate-500 leading-snug">
            The other device must have USB debugging on and will ask you to authorize this phone the first time. Nothing here bypasses that prompt.
          </p>
          <div className="flex flex-wrap gap-1.5">
            <button className={btn} disabled={!!busy} onClick={() => run('adb', async () => { const r: any = await bastetRpc('usb.adb_connect', {}); setAdbOut(r.ok ? `Connected: ${r.peerBanner || ''}` : r.error || 'Connect failed'); })}>
              <Plug className="w-3 h-3 text-sky-400" /> Connect / authorize
            </button>
            {['getprop ro.product.model', 'dumpsys battery', 'pm list packages -3', 'ip addr show wlan0', 'settings get global adb_enabled'].map((c) => (
              <button key={c} className={btn} disabled={!!busy} onClick={() => { setAdbCmd(c); runAdb(c); }}>
                {c.split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
            <button
              className={btn}
              disabled={!!busy}
              onClick={() => run('adb', async () => { const r: any = await bastetRpc('usb.adb_tcpip', { port: 5555 }); setAdbOut(JSON.stringify(r, null, 2)); })}
            >
              <Zap className="w-3 h-3 text-amber-400" /> Wireless ADB :5555
            </button>
          </div>
          <form className="flex gap-1.5" onSubmit={(e) => { e.preventDefault(); runAdb(adbCmd); }}>
            <input value={adbCmd} onChange={(e) => setAdbCmd(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-sky-400/60" />
            <button type="submit" disabled={!!busy} className={btn}><TerminalIcon className="w-3 h-3" /> Run</button>
          </form>
          {adbOut && <pre className="bg-black/60 rounded-lg p-2.5 text-[10px] text-emerald-200 font-mono whitespace-pre-wrap break-words max-h-56 overflow-auto">{adbOut}</pre>}
        </div>
      )}

      {/* Serial controls */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] text-slate-500 font-mono space-y-1">
            BAUD
            <select value={baud} onChange={(e) => { const b = parseInt(e.target.value, 10); setBaud(b); applyLine(b, framing); }}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-2 py-2 text-xs text-white font-mono">
              {BAUDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label className="text-[10px] text-slate-500 font-mono space-y-1">
            FRAMING
            <select value={framing} onChange={(e) => { setFraming(e.target.value); applyLine(baud, e.target.value); }}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-2 py-2 text-xs text-white font-mono">
              {Object.keys(FRAMINGS).map((k) => <option key={k} value={k}>{FRAMINGS[k].label}</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button className={btn} disabled={!connected || !!busy} onClick={onAutoBaud}>
            {busy === 'autobaud' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 text-fissal-gold" />} Auto-detect baud
          </button>
          <button className={`${btn} ${dtr ? '!bg-emerald-500/25' : ''}`} onClick={() => toggleLine('DTR')}>DTR {dtr ? 'HIGH' : 'low'}</button>
          <button className={`${btn} ${rts ? '!bg-emerald-500/25' : ''}`} onClick={() => toggleLine('RTS')}>RTS {rts ? 'HIGH' : 'low'}</button>
          <button className={btn} disabled={!connected || !!busy} onClick={pulseReset}>Reset (RTS)</button>
          <button className={btn} disabled={!connected || !!busy} onClick={espBootloader}>ESP bootloader</button>
          <button className={btn} disabled={!connected || !!busy} onClick={onBreak}>Break</button>
          <button className={btn} disabled={!connected || !!busy} onClick={() => run('purge', async () => { await purgeUsbBuffers(); sys('Hardware buffers purged'); })}>Purge</button>
        </div>
        {stats && (
          <div className="grid grid-cols-4 gap-1.5 text-[10px] font-mono">
            <Stat label="RX" value={fmtBytes(stats.rxBytes)} sub={`${stats.rxBytesPerSec.toFixed(0)} B/s`} />
            <Stat label="TX" value={fmtBytes(stats.txBytes)} sub={`${stats.txBytesPerSec.toFixed(0)} B/s`} />
            <Stat label="Errors" value={`${stats.errors}`} sub={stats.msSinceLastRx >= 0 ? `idle ${(stats.msSinceLastRx / 1000).toFixed(1)}s` : 'no rx yet'} />
            <Stat label="Up" value={`${Math.floor(stats.uptimeMs / 1000)}s`} sub={`${stats.baudRate} bd`} />
          </div>
        )}
      </div>

      {/* Console */}
      <div className="glass-panel rounded-2xl border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-black/30">
          <div className="flex gap-1">
            {(['text', 'hex'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${view === v ? 'bg-fissal-gold text-slate-950' : 'text-slate-400 hover:text-white'}`}>{v}</button>
            ))}
          </div>
          <div className="flex gap-1.5">
            <button className="p-1.5 rounded-md hover:bg-white/10 text-slate-300" onClick={copyAll} aria-label="Copy log"><Copy className="w-3.5 h-3.5" /></button>
            <button className="p-1.5 rounded-md hover:bg-white/10 text-slate-300" onClick={() => setChunks([])} aria-label="Clear"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <div
          ref={scroller}
          onScroll={(e) => {
            const el = e.currentTarget;
            stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          }}
          className="h-56 overflow-auto bg-black/70 p-2.5"
        >
          <pre className="text-[11px] leading-snug text-emerald-200 font-mono whitespace-pre-wrap break-all">{rendered || 'Console is empty. Incoming serial data appears here live.'}</pre>
        </div>
        <form className="p-2.5 border-t border-white/10 bg-black/30 space-y-2" onSubmit={(e) => { e.preventDefault(); onSend(); }}>
          <div className="flex gap-1.5">
            <input value={input} onChange={(e) => setInput(e.target.value)} disabled={!connected}
              placeholder={hexInput ? 'hex bytes, e.g. 7E 06 01' : connected ? 'type a command' : 'connect first'}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="flex-1 min-w-0 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white disabled:opacity-50 focus:outline-none focus:border-fissal-gold/60" />
            <button type="submit" disabled={!connected} className="px-3 rounded-lg bg-fissal-gold text-slate-950 disabled:opacity-40"><Send className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-400 font-mono">
            <label className="flex items-center gap-1"><input type="checkbox" checked={hexInput} onChange={(e) => setHexInput(e.target.checked)} /> HEX</label>
            <label className="flex items-center gap-1">
              end
              <select value={ending} onChange={(e) => setEnding(e.target.value as any)} disabled={hexInput}
                className="bg-black/50 border border-white/10 rounded px-1 py-0.5 text-white">
                <option value="none">none</option><option value="cr">CR</option><option value="lf">LF</option><option value="crlf">CR+LF</option>
              </select>
            </label>
          </div>
        </form>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; sub: string }> = ({ label, value, sub }) => (
  <div className="rounded-lg bg-black/40 border border-white/10 px-2 py-1.5 text-center">
    <div className="text-slate-500">{label}</div>
    <div className="text-slate-100 font-bold">{value}</div>
    <div className="text-slate-500">{sub}</div>
  </div>
);
